import React, { useState, useEffect, memo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import styled from "styled-components"

import Layout from "../../zzz/layout"
import SEO from "../SEO/seo"
import Heading from "../../zzz/organisms/heading"
import Inner from "../../zzz/layout/pageInner/Inner"
import Block from "./Block"
import Results from "./Results"
import EllipsisLoader from "../../zzz/organisms/ellipsisLoader"

import ProductList from "../Products/ProductList"
import Social from "../../zzz/organisms/blog/single/Social"

import { shippingClassGet, getImage } from "../../lib/util"

import ImageOne from "../../images/Mattress-Firm.gif"
import ImageTwo from "../../images/Mattress-Medium.gif"
import ImageThree from "../../images/Mattress-Plush.gif"

const maxFilterAmount = 100

// ======================
// 	🧱🧱 COMPONENT 🧱🧱
// ======================
const BedFinder = memo(
  ({
    location,
    defaultProductType = "beds",
    post_id,
    seo,
    subTitle,
    featuredImage,
  }) => {
    const graphqlData = useStaticQuery(BEDFINDER_PRODUCTS)
    const bedFinderProducts = graphqlData.allWcProducts.nodes

    const titleOverloadOptions = {
      beds: "Bed Finder",
      mattresses: "Mattress Finder",
    }

    const [titleOverload, setTitleOverload] = useState(
      titleOverloadOptions[defaultProductType]
    )
    const [sharePath, setSharePath] = useState(
      typeof window != "undefined" && window.location
        ? window.location.pathname
        : location.pathname
    )
    const [filters, setFilters] = useState(initFromBlocks(blocks))
    const [blocksMapped] = useState(mapBlocks(blocks))
    const [visible, setVisible] = useState(1)
    const [navigatedUrlParsed, setNavigatedUrlParsed] = useState(false)
    const [loadingBlocksState, setLoadingBlocksState] = useState(true)
    const [resultCount, setResultCount] = useState(bedFinderProducts.length)

    // Hide weight_capacity option unless Main Bedroom is selected
    let visibleBlocks = blocks.filter(
      (bl) => filters.bedroom_type.main.active || bl.key !== "weight_capacity"
    )
    visibleBlocks = visibleBlocks.slice(0, visible)

    useEffect(() => {
      let newLoadingBlocksState = false
      if (typeof window != "undefined") {
        if (typeof window.location != "undefined") {
          if (typeof window.location.search != "undefined") {
            if (window.location.search.length) {
              if (!navigatedUrlParsed) {
                newLoadingBlocksState = true
              }
            }
          }
        }
      }
      setLoadingBlocksState(newLoadingBlocksState)
    }, [navigatedUrlParsed])

    const viewNext = (position) =>
      setVisible((current) => (position === current ? current + 1 : current))

    const isActive = (blockKey) => (optionKey) =>
      filters[blockKey][optionKey]["active"]

    // set selected item as active/not active
    const toggleFilter = (position) => (blockKey) => (optionKey) => () => {
      viewNext(position)
      setFilters((prev) => {
        const newFilters = {
          ...prev,
          [blockKey]: {
            ...prev[blockKey],
            [optionKey]: {
              ...prev[blockKey][optionKey],
              active: !prev[blockKey][optionKey]["active"],
            },
          },
        }
        // For exclusive filters, disable other options when one is selected
        const keys = Object.keys(newFilters[blockKey])
        for (let i = 0; i < keys.length; i++) {
          if (
            keys[i] !== optionKey &&
            newFilters[blockKey][keys[i]]["exclusive"]
          ) {
            newFilters[blockKey][keys[i]]["active"] = false
          }
        }
        return newFilters
      })
    }

    useEffect(() => {
      if (
        typeof HTMLIFrameElement != "undefined" &&
        window.location &&
        window.location.pathname &&
        history
      ) {
        // if the user just navigated to this URL, then build the filter from the URL
        if (!navigatedUrlParsed) {
          let queryString = `product_type=${defaultProductType}`
          if (window.location.search) {
            queryString = window.location.search.replace("?", "")
          }
          const newFilters = filters
          const filterBlockArray = queryString.split("&")
          let indexOfUrlParameter = 0
          filterBlockArray.forEach((filterBlock) => {
            if (filterBlock.length) {
              const blockEntry = filterBlock.split("=")
              if (blockEntry.length > 1) {
                const blockKey = blockEntry[0]
                const blockVal = blockEntry[1]
                if (newFilters[blockKey]) {
                  blockVal.split("+").forEach((optionKey) => {
                    if (optionKey.length) {
                      if (newFilters[blockKey][optionKey]) {
                        newFilters[blockKey][optionKey].active = true
                        if (
                          blocksMapped[blockKey].index >= indexOfUrlParameter
                        ) {
                          indexOfUrlParameter = blocksMapped[blockKey].index + 1
                        }
                      }
                    }
                  })
                }
              }
            }
          })
          setFilters(newFilters)
          setVisible(indexOfUrlParameter + 1)
          setSharePath(`${window.location.pathname}${window.location.search}`)
        } else {
          // if the filter changes, update the URL for sharing
          let pathname = window.location.pathname
          const filter_keys = Object.keys(filters)
          const filter_args = []

          for (
            let filter_keys_i = 0;
            filter_keys_i < filter_keys.length;
            ++filter_keys_i
          ) {
            const filter_key = filter_keys[filter_keys_i]
            const filter_item = filters[filter_key]
            const filter_item_keys = Object.keys(filter_item)
            const filter_item_args = []
            for (
              let item_keys_i = 0;
              item_keys_i < filter_item_keys.length;
              ++item_keys_i
            ) {
              const item_key = filter_item_keys[item_keys_i]
              const item_content = filter_item[item_key]
              const item_state = item_content.active
              if (item_state) {
                filter_item_args.push(item_key)
              }
            }
            if (filter_item_args.length) {
              filter_args.push(`${filter_key}=${filter_item_args.join("+")}`)
            }
          }
          let filter_search_string = ""
          if (filter_args.length) {
            filter_search_string = `?${filter_args.join("&")}`
            let newPath = `${pathname}${filter_search_string}`
            history.replaceState(null, "", newPath)
            setSharePath(newPath)
          } else {
            history.replaceState(null, "", window.location.pathname)
            setSharePath(window.location.pathname)
          }
        }
      }
      setNavigatedUrlParsed(true)
    }, [filters])

    const filteredItems = React.useMemo(() => {
      // use visible blocks to determine which options have been seen/are valid to filter on
      // and get the relavant filter predicate functions
      const selectedFilters = visibleBlocks.reduce((prev, { key }) => {
        const items =
          typeof filters != "object" ? [] : Object.values(filters[key])
        const filterFns = items
          .filter((it) => it.active)
          .map((it) => it.filterFn)
        return filterFns.length ? [...prev, { key, filterFns }] : prev
      }, [])

      // run the filter functions on all products to get a list of all passing products
      const refined = bedFinderProducts.filter(
        ({ acf, categories, shipping_class, ...rest }) => {
          // between blocks we use an AND
          // between items in a block we use OR
          return selectedFilters.every(({ key, filterFns }) => {
            // Use category to filter by product t
            if ("product_type" === key) {
              return Object.entries(filters["product_type"]).some(
                ([productTypeFilterKey, productTypeFilterValue]) => {
                  return (
                    productTypeFilterValue.active &&
                    categories.some((cat) => cat.slug == productTypeFilterKey)
                  )
                }
              )
            }

            const acf_value = acf[key]
            if (!acf_value) {
              return true
            }

            const acf_value_array = Array.isArray(acf_value)
              ? acf_value
              : [acf_value]

            return acf_value_array.some((value) => {
              return filterFns.some((fn) => {
                const value_slug = toSlug(value.slug || value)
                return fn(value_slug)
              })
            })
          })
        }
      )
      return refined
    }, [filters, visibleBlocks, sharePath])

    const selectedSizesObject = React.useMemo(() => {
      const sizeKeys = []
      const sizeOptions = []
      const sizeFilters = Object.entries(filters["sizes"])
      const xlFiltersArray = Object.entries(filters["mattress_length"])
      const xlFiltersObject = xlFiltersArray.reduce(function (acc, cur) {
        acc[cur[0]] = cur[1].active
        return acc
      }, {})

      const isLengthStandard = !!xlFiltersObject["standard_length"]
      const isLengthExtra = !!xlFiltersObject["extra_length"]
      const selectBoth = isLengthStandard == isLengthExtra

      const allSizeOptions = blocksMapped.sizes.options.reduce(function (
        acc,
        cur
      ) {
        acc[cur.value] = cur
        return acc
      },
      {})

      for (let i = 0; i < sizeFilters.length; ++i) {
        const sizeKey = sizeFilters[i][0]
        const isSizeActive = sizeFilters[i][1].active
        if (sizeKey != "undefined") {
          const newOption = { ...allSizeOptions[sizeKey] }
          if (selectBoth) {
            newOption.description = `${newOption.description} wide`
          } else if (isLengthStandard) {
            newOption.description = `${newOption.description} x 188 cm`
          } else if (isLengthExtra) {
            newOption.description = `${newOption.description} x 200 cm`
          }
          sizeOptions.push(newOption)
          if (isSizeActive) {
            if (selectBoth || isLengthStandard) {
              sizeKeys.push(sizeKey)
            }
            if (selectBoth || isLengthExtra) {
              sizeKeys.push(`${sizeKey}-xl`)
            }
          }
        } else {
          sizeOptions.push(allSizeOptions[sizeKey])
        }
      }

      return { sizeKeys, sizeOptions }
    }, [filters, sharePath])

    // // Debuggin code start!!!
    //
    //  // <debug>

    // const [previousRemovedNodes, setPreviousRemovedNodes] = useState([])
    // const [previousRemovedCount, setPreviousRemovedCount] = useState(0)

    // const containsObject = (obj, list) => {
    //   for (const listItem of list) {
    //     if (listItem === obj) {
    //       return true;
    //     }
    //   }
    //   return false;
    // }
    // // const removedItems = bedFinderProducts.filter(node => !containsObject(node, filteredItems))
    // const removedItems = []
    // if (removedItems.length != previousRemovedCount) {
    //   const newRemovedItems = removedItems.filter(node => !containsObject(node, previousRemovedNodes))
    //   const newAddedItems = previousRemovedNodes.filter(node => !containsObject(node, removedItems))
    //   console.log("DEBUG visibleBlocks = ", visibleBlocks)
    //   console.log("DEBUG filters = ", filters)
    //   console.log("DEBUG bedFinderProducts = ", bedFinderProducts)
    //   console.log("DEBUG filteredItems = ", filteredItems)
    //   console.log("DEBUG removedItems = ", removedItems)
    //   console.log("DEBUG items removed ======== ", newRemovedItems.length)
    //   console.log("DEBUG newRemovedItems = ", newRemovedItems)
    //   console.log("DEBUG items added ======== ", newAddedItems.length)
    //   console.log("DEBUG newAddedItems = ", newAddedItems)
    //   setPreviousRemovedNodes(removedItems)
    //   setPreviousRemovedCount(removedItems.length)
    // }

    // useEffect(() => {
    //   console.log("DEBUG useEffect[filters] filters = ", filters)
    // }, [filters])

    // // Debuggin code end!!!
    //
    //  // </debug>
    //

    useEffect(() => {
      try {
        if (
          !filters["product_type"]["beds"]["active"] &&
          filters["product_type"]["mattresses"]["active"]
        ) {
          setTitleOverload(titleOverloadOptions.mattresses)
        } else if (titleOverload != titleOverloadOptions.beds) {
          setTitleOverload(titleOverloadOptions.beds)
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }, [filters])

    const shippingFilter = []
    if (filters && typeof filters.shipping_class != "undefined") {
      Object.entries(filters.shipping_class).forEach(([ship, state]) => {
        if (state.active) {
          shippingFilter.push(ship)
        }
      })
    }

    return (
      <Layout location={location}>
        <SEO
          title="Bed Finder"
          location={location}
          post_id={post_id}
          seo={seo}
        />
        <Heading
          titleOverload={titleOverload}
          image={getImage(featuredImage)}
          subTitle={subTitle}
          location={location}
        />
        <Grey>
          {loadingBlocksState ? (
            <EllipsisLoader />
          ) : (
            <Inner>
              <SplitFiltersAndCounter>
                <Left>
                  {visibleBlocks.map((block, i) => {
                    const blockOptions =
                      block.key === "sizes"
                        ? selectedSizesObject.sizeOptions
                        : block.options
                    return (
                      <EntranceStyling key={`${i}-${block.title}`}>
                        <Block
                          title={block.title}
                          image={block.image}
                          options={blockOptions}
                          extra={block.extra}
                          toggleFilter={toggleFilter(i + 1)(block.key)}
                          filterActive={isActive(block.key)}
                          isLast={i === blocks.length - 1}
                          viewNext={() => viewNext(i + 1)}
                        />
                      </EntranceStyling>
                    )
                  })}
                </Left>
                <Right>
                  <Results amount={resultCount} limit={maxFilterAmount} />
                </Right>
              </SplitFiltersAndCounter>
              <Items showItems={resultCount <= maxFilterAmount}>
                <ProductList
                  productList={filteredItems}
                  sizesFilter={selectedSizesObject.sizeKeys}
                  shippingFilter={shippingFilter}
                  sort={["price-low-to-high"]}
                  setParentProductListLength={setResultCount}
                />
              </Items>
              <SocialContainer
                className={`${
                  resultCount &&
                  resultCount <= maxFilterAmount &&
                  "productsVisible"
                }`}
              >
                <Social
                  location={location}
                  slug={location.pathname}
                  title={"Bed Finder"}
                  shareQueryString
                  sharePath={sharePath}
                />
              </SocialContainer>
            </Inner>
          )}
        </Grey>
      </Layout>
    )
  }
)

const blocks = [
  {
    title: "What type of product are you looking for?",
    key: "product_type",
    options: [
      {
        title: "Bed",
        description: "Full bed set with mattress and matching base",
        value: "beds",
      },
      {
        title: "Mattress",
        description: "Mattress only, for when you already have a base",
        value: "mattresses",
      },
    ],
  },
  {
    title: "Which bedroom do you need this bed for?",
    key: "bedroom_type",
    options: [
      {
        title: "Main Bedroom",
        value: "main",
      },
      {
        title: "Guest Bedroom",
        value: "guest",
      },
      {
        title: "Kids Bedroom",
        value: "kids",
      },
    ],
  },
  {
    title: "Which mattress length are you looking for?",
    key: "mattress_length",
    options: [
      {
        title: "Standard Length",
        description: "188 cm long",
        value: "standard_length",
      },
      {
        title: "Extra Length",
        description: "200 cm long",
        value: "extra_length",
      },
      {
        title: "Not Sure?",
        description: "Click here to check out our bed size guide!",
        link: "/bed-sizes/",
      },
    ],
    extra:
      "We recommend the mattress being at least 10 cm longer than the tallest user.",
  },
  {
    title: "What size bed would you like?",
    key: "sizes",
    options: [
      {
        title: "Single",
        description: "92 cm",
        value: "single",
      },
      {
        title: "Three Quarter",
        description: "107 cm",
        value: "three-quarter",
      },
      {
        title: "Double",
        description: "137 cm",
        value: "double",
      },
      {
        title: "Queen",
        description: "152 cm",
        value: "queen",
      },
      {
        title: "King",
        description: "183 cm",
        value: "king",
      },
      {
        title: "Not Sure?",
        description: "Click here to check out our bed size guide!",
        link: "/bed-sizes/",
      },
    ],
  },
  {
    title: "What level of support would you like?",
    key: "comfort_level",
    options: [
      {
        title: "Firm",
        image: ImageOne,
        description:
          "This bed is similar to a 1 rating, in terms of keeping your spine aligned and sleeping on your back. However, a 2 is also suitable for stomach sleepers who enjoy a little more firmness. Such a mattress does offer a little more padding to stomach and back sleepers, but it is usually a little too hard for you side folk.",
        value: "firm",
      },
      {
        title: "Medium",
        image: ImageTwo,
        description:
          "Beds in this comfort scale usually fits most people’s needs. 3 Rating beds can be seen as the equilibrium of the sleeping styles. They are perfect for guest bedrooms and are also well suited for the hospitality industry. This bed is normally soft enough for the average side sleeper and supportive enough for back and stomach sleepers.",
        value: "medium",
      },
      {
        title: "Plush",
        image: ImageThree,
        description:
          "This comfort rating is ideal for side sleepers, as well as back sleepers who want to experience the sensation of being embraced by the mattress. A 4 comfort level mattress should not be considered by a stomach sleeper, as the softness will cause an unwanted arch in the back. A mattress like this focuses less on back support and more on pressure relief.",
        value: "plush",
      },
      {
        title: "Not Sure?",
        description: "Click here to check out our bed size guide!",
        link: "/how-is-mattress-firmness-measured/",
      },
    ],
  },
  {
    title: "Which mattress type would you like?",
    key: "mattress_type",
    options: [
      {
        title: "Foam",
        value: "foam",
      },
      {
        title: "Bonnell Spring",
        value: "bonnell-spring",
      },
      {
        title: "Hybrid",
        value: "hybrid",
      },
      {
        title: "Pocket Spring",
        value: "pocket-spring",
      },
    ],
  },
  {
    title:
      "What is the weight of the heaviest person that will sleep on the bed?",
    key: "weight_capacity",
    exclusive: true, // Exclusive means selecting one of these will disable the others
    options: [
      {
        title: "Up to 90kg",
        value: "below-90",
        filterOptions: [
          {
            // Any mattress rated 0kg or more will work
            value: 0,
            relation: "greater",
          },
        ],
      },
      {
        title: "Up to 110kg",
        value: "below-110",
        filterOptions: [
          {
            // Any mattress rated 120kg or more will work
            value: 110,
            relation: "greater-or-equal",
          },
        ],
      },
      {
        title: "Up to 150kg",
        value: "below-150",
        filterOptions: [
          {
            value: 150,
            relation: "greater-or-equal",
          },
        ],
      },
      {
        title: "Above 150kg",
        value: "over-150",
        filterOptions: [
          {
            value: 150,
            relation: "greater",
          },
        ],
      },
    ],
  },
  {
    title: "How urgently do you need this product?",
    key: "shipping_class",
    options: [
      {
        title: `${shippingClassGet("fast-track-shipping").delay} business days`,
        value: "fast-track-shipping",
      },
      {
        title: `${shippingClassGet("fallback").delay} business days`,
        value: "default",
      },
      {
        title: `${shippingClassGet("delayed-shipping").delay} business days`,
        value: "delayed-shipping",
      },
    ],
  },
]

// ======================
// 	 🔧🔧 HELPERS 🔧🔧
// ======================

// prettier-ignore
const toSlug = str => str.toLowerCase().split(" ").join("-")

// these match acf options in wordpress
// f = first
// s = second
const equalityFns = {
  equal: (f) => (s) => f === s,
  greater: (f) => (s) => s > f,
  less: (f) => (s) => s < f,
  "greater-or-equal": (f) => (s) => s >= f,
  "less-or-equal": (f) => (s) => s <= f,
}

const mapBlocks = (blocks) => {
  return blocks.reduce(function (acc, cur) {
    const index = Object.keys(acc).length
    acc[cur.key] = { index, ...cur }
    return acc
  }, {})
}

// create state from wordpress blocks acf
const initFromBlocks = (blocks) =>
  blocks.reduce((acc, { key, options, exclusive = false }) => {
    return {
      ...acc,
      [key]: {
        ...options.reduce((acc, { value, filterOptions }) => {
          return {
            ...acc,
            [value]: {
              active: false,
              exclusive,
              // filter functions will use the selected items keys and create predicate functions str => str.includes(val)
              // or it'll use an equality function if that value was selcted in wordpress (used for weight ranges)
              filterFn: filterOptions
                ? (val) =>
                    filterOptions.every(({ value, relation }) => {
                      return equalityFns[relation](value)(val)
                    })
                : (val) => {
                    return toSlug(val).includes(toSlug(value))
                  },
            },
          }
        }, {}),
      },
    }
  }, {})

// ====================
// 	💅🏼💅🏼 STYLES 💅🏼💅🏼
// ====================

const Items = styled.div`
  ${({ showItems }) => (showItems ? "" : "display: none;")};
  background: #f6f6f6;
  padding-top: 20px;
`

const Grey = styled.div`
  padding: 60px 0;
  background: #f6f6f6;
  transition: 0.2s all ease-in-out;
`

const SplitFiltersAndCounter = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  grid-gap: 20px;
  @media (max-width: ${({ theme }) => theme.breakLarge}) {
    grid-template-columns: 1fr 250px;
  }
  @media (max-width: ${({ theme }) => theme.breakSmall}) {
    grid-template-columns: 1fr;
  }
`
const Left = styled.div``
const Right = styled.div``

const SocialContainer = styled.div`
  padding-top: 40px;
  margin-top: 0px;
  &.productsVisible {
    padding-top: 10px;
  }
`

const EntranceStyling = styled.div`
  animation: slidein 0.2s ease-in-out;
  @keyframes slidein {
    from {
      transform: translateX(-100px);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`

// ==================
//        QUERY
// ==================

const BEDFINDER_PRODUCTS = graphql`
  {
    ## ProductFilter: All beds and mattresses products
    allWcProducts(
      filter: {
        status: { eq: "publish" }
        catalog_visibility: { ne: "hidden" }
        categories: { elemMatch: { slug: { in: ["beds", "mattresses"] } } }
      }
    ) {
      nodes {
        ...ProductCardInfo
        acf {
          bedroom_type
          mattress_type
          comfort_level
          weight_capacity
          sizes
        }
      }
    }
  }
`

// const IMAGE_ONE = graphql`
//   {
//     file(relativePath: { eq: "lady-in-bed.png" }) {
//       childImageSharp {
//         gatsbyImageData
//       }
//     }
//   }
// `

// const IMAGE_TWO = graphql`
//   {
//     file(relativePath: { eq: "lady-in-bed.png" }) {
//       childImageSharp {
//         gatsbyImageData
//       }
//     }
//   }
// `

// const IMAGE_THREE = graphql`
//   {
//     file(relativePath: { eq: "lady-in-bed.png" }) {
//       childImageSharp {
//         gatsbyImageData
//       }
//     }
//   }
// `

export default BedFinder
